import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError, notify } from '../notify';

// Accounts in Karma farming that have reached the posting stage. Remembered
// per account (instant when coming back to it) and re-checked on every switch;
// known(id) is false until the first answer for that account is in.
const karmaPostingCache = new Map();

export function useKarmaPostingAccounts(accountIds) {
  const [, setChecked] = useState(0);
  const key = (accountIds || []).join(',');
  useEffect(() => {
    let alive = true;
    const ids = key ? key.split(',') : [];
    Promise.all(ids.map(id => axios.get(`${API}/api/accounts/${id}/strategy`)
      .then(({ data }) => { karmaPostingCache.set(id, data?.current?.phase === 'karma' && data?.karma?.stage === 'posts'); })
      .catch(() => { if (!karmaPostingCache.has(id)) karmaPostingCache.set(id, false); })))
      .then(() => { if (alive) setChecked(n => n + 1); });
    return () => { alive = false; };
  }, [key]);
  return {
    has: (id) => karmaPostingCache.get(id) === true,
    known: (id) => karmaPostingCache.has(id),
  };
}

export const isKarmaIdeaContent = (post) => /\/karma-ideas\//.test(post?.content_url || '');

export function KarmaModeNotice({ accounts, postingIds }) {
  const names = (accounts || []).filter(a => postingIds.has(a.id)).map(a => `u/${a.reddit_username}`);
  if (!names.length) return null;
  return (
    <div style={{ padding: '8px 12px', marginBottom: '12px', background: '#1a1400', border: '1px solid #92400e', borderRadius: '8px', fontSize: '12px', color: '#f59e0b' }}>
      🌱 Karma farming mode: {names.join(', ')}. Fill each post with <b>✨ Generate post</b>; <b>🎲 Regenerate</b> if you don't like it.
    </div>
  );
}

// Fills one scheduled post with a spoofed top image from its sub + a rewritten title
export function KarmaGenerateButton({ session, post, regenerate = false, onDone, style }) {
  const [busy, setBusy] = useState(false);
  const sub = post.subreddits?.name;
  if (!sub) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/karma/ideas/generate`,
        { account_id: session.account_id, subreddit: sub, scheduled_post_id: post.id }, { timeout: 120000 });
      if (data.post) onDone(data.post);
      if (data.error) notify(data.error);
    } catch (e) { notifyError(e, 'Generate failed'); }
    setBusy(false);
  };
  return (
    <button onClick={run} disabled={busy} className={regenerate ? 'btn-secondary' : undefined}
      title="A spoofed image from this sub's top posts (not its top 30 of the year/all time) with a rewritten title"
      style={regenerate ? { fontSize: '11px', padding: '3px 10px', ...style } : {
        whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px', background: '#2a1a00', color: '#f59e0b',
        border: '1px solid #f59e0b', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', ...style }}>
      {busy ? '⏳ Generating…' : regenerate ? '🎲 Regenerate' : '✨ Generate post'}
    </button>
  );
}
